import FavoriteIcon from '@mui/icons-material/Favorite';
import FileDownloadTwoToneIcon from '@mui/icons-material/FileDownloadTwoTone';
import PlayCircleTwoToneIcon from '@mui/icons-material/PlayCircleTwoTone';
import WatchIcon from '@mui/icons-material/Watch';
import { alpha, Box, ImageListItem, ImageListItemBar, LinearProgress, Skeleton, Stack, Theme, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import 'react-h5-audio-player/lib/styles.css';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import { VaultMediaResponseList } from '../../../../hooks/useSubscriberVaultMedias';
import { useDownloadManager } from '../../../../store/downloadManager/useDownloadManager';
import { UserContext } from '../../../../store/UserContext';
import { isSextforceActive, mediaTypeIcon, secondsToMinutesAndSecondsString } from '../../../../utils/common';
import MediaLightbox from '../../../common/MediaLightbox';

type Props = {
    subscriber: OnlyFansSubscriber;
    medias: OnlyFansVaultMedias.RestructuredResponseItem[];
    item: VaultMediaResponseList;
    thumbnails: any[];
    handleMediaClicked: (albumMedia: VaultMediaResponseList) => void;
    setSelectedAudioUrl: (url: string) => void;
    setPlayerOpen: (open: boolean) => void;
    isLargeScreen?: boolean;
    disabled?: boolean;
};

const MessageUsersFormAddMediasSelectorImageListItem = (props: Props) => {
    const { subscriber, medias, item, thumbnails, handleMediaClicked, isLargeScreen, disabled } = props;

    const userContext = useContext(UserContext);

    const theme: Theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const { current, isDownloading, progress, addToQueue } = useDownloadManager();

    const [lightboxOpen, setLightboxOpen] = useState(false);

    const foundMedia = medias.find(media => media.id === item.id);

    const handleMediaDownload = async (albumMedia: VaultMediaResponseList) => {
        if (disabled || !albumMedia.full) {
            return;
        }

        // Add the media to the download queue
        addToQueue({
            subscriberId: subscriber._id!,
            username: subscriber.username,
            media: albumMedia,
        });

        enqueueSnackbar('Media added to download queue', { variant: 'success' });
    };

    const allowDownload = isSextforceActive(subscriber) || userContext?.isAdmin;

    const imageWidth = isLargeScreen ? 166 : 118;
    const imageHeight = isLargeScreen ? 162 : 118;

    return (
        <ImageListItem
            sx={{
                cursor: disabled ? 'default' : 'pointer',
                borderStyle: 'solid',
                borderWidth: '2px',
                borderColor: foundMedia ? theme.palette.primary.main : theme.palette.common.white,
                ':hover': disabled ? {} : { borderStyle: 'solid', borderWidth: '2px', borderColor: theme.palette.secondary.main },
                ...(disabled && {
                    opacity: 0.5,
                }),
            }}
        >
            {isDownloading && current && current.media.id === item.id && (
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        backgroundColor: theme.palette.grey[300],
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.secondary.main,
                        },
                    }}
                />
            )}
            {isDownloading && current && current.media.id === item.id && (
                <Typography
                    component="div"
                    variant="body2"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        color: theme.palette.common.white,
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: '100%',
                        pointerEvents: 'none',
                    }}
                >
                    {progress >= 0 ? `${progress}%` : 'Downloading...'}
                </Typography>
            )}
            {allowDownload && (
                <FileDownloadTwoToneIcon
                    sx={{
                        position: 'absolute',
                        top: theme.spacing(0.5),
                        right: theme.spacing(0.5),
                        color: item.full ? theme.palette.common.white : theme.palette.text.disabled,
                        // make stand out when on white background
                        // backgroundColor: alpha(theme.palette.common.black, 0.5),
                        cursor: item.full ? 'pointer' : 'default',
                        opacity: 0.7,
                        transition: 'color 0.3s, transform 0.3s',
                        ...(item.full && {
                            ':hover': {
                                color: theme.palette.secondary.main,
                                opacity: 1,
                                transition: 'transform 0.2s',
                                transform: 'scale(1.1)',
                            },
                        }),
                    }}
                    onClick={e => {
                        e.stopPropagation();

                        if (item.full) {
                            handleMediaDownload(item);
                        } else {
                            enqueueSnackbar('Media not available for download', { variant: 'warning' });
                        }
                    }}
                />
            )}
            {item.full && (
                <Box>
                    <PlayCircleTwoToneIcon
                        onClick={e => {
                            e.stopPropagation();
                            if (item.full && !disabled) {
                                setLightboxOpen(true);
                            }
                        }}
                        // fontSize="large"
                        sx={{
                            position: 'absolute',
                            top: theme.spacing(0.5),
                            left: theme.spacing(0.5),
                            color: theme.palette.common.white,
                            cursor: 'pointer',
                            opacity: 0.7,
                            transition: 'color 0.3s, transform 0.3s',
                            transform: 'scale(1.2)',
                            ':hover': {
                                color: theme.palette.secondary.main,
                                opacity: 1,
                                transition: 'transform 0.2s',
                            },
                        }}
                    />
                    {lightboxOpen && (
                        <MediaLightbox
                            subscriber={subscriber}
                            open={lightboxOpen}
                            onClose={() => setLightboxOpen(false)}
                            item={item}
                            type={item.type}
                            title={item.type !== 'audio' ? `${item.id} - ${item.full.width}x${item.full.height}` : ''}
                        />
                    )}
                </Box>
            )}
            {item.cachedThumb ? (
                <img
                    src={item.cachedThumb}
                    srcSet={item.cachedThumb}
                    alt={item.id.toString()}
                    loading="lazy"
                    width={imageWidth}
                    height={imageHeight}
                    onClick={() => {
                        handleMediaClicked(item);
                    }}
                />
            ) : thumbnails.find(thumbnail => thumbnail.thumbId === item.id && thumbnail.blob) ? (
                <img
                    src={URL.createObjectURL(thumbnails.find(thumbnail => thumbnail.thumbId === item.id && thumbnail.blob).blob)}
                    srcSet={thumbnails.find(thumbnail => thumbnail.thumbId === item.id && thumbnail.blob).blob}
                    alt={item.id.toString()}
                    loading="lazy"
                    width={imageWidth}
                    height={imageHeight}
                    onClick={() => {
                        handleMediaClicked(item);
                    }}
                />
            ) : item.thumb ? (
                <Skeleton variant="rectangular" width={imageWidth} height={imageHeight} />
            ) : (
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[300],
                        color: theme.palette.text.secondary,
                        fontSize: '0.8rem',
                        textAlign: 'center',
                    }}
                >
                    no thumbnail
                </Box>
            )}
            {/* ): (
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        backgroundColor: theme.palette.grey[300],
                        height: '100%',
                    }}
                    onClick={() => handleMediaClicked(item)}
                >
                    <Grid item xs={12} sx={{ paddingBottom: theme.spacing(5) }}>
                        <Grid container justifyContent="center" alignItems="center">
                            <Grid item xs={12}>
                                <div style={{ textAlign: 'center' }}>
                                    <PlayCircleTwoToneIcon
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (item.src && !disabled) {
                                                setSelectedAudioUrl(item.src);
                                                setPlayerOpen(true);
                                            }
                                        }}
                                        fontSize="large"
                                        sx={{
                                            ':hover': {
                                                color: theme.palette.secondary.main,
                                            },
                                            height: theme.spacing(4),
                                        }}
                                    />
                                </div>
                            </Grid>
                            {/* <Grid item xs={12}>
                            <div style={{ textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                                    {secondsToMinutesAndSecondsString(item.duration || 0)}
                                </Typography>
                            </div>
                        </Grid> */}
            {/* </Grid>
                    </Grid>
                </Grid>
            )}} */}
            <ImageListItemBar
                title={moment(item.createdAt).calendar({
                    sameDay: '[Today]',
                    nextDay: '[Tomorrow]',
                    nextWeek: 'MMM Do',
                    lastDay: '[Yesterday]',
                    lastWeek: 'MMM Do',
                    sameElse: 'MMM Do',
                })}
                subtitle={
                    <Stack direction="column" spacing={0.5}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <FavoriteIcon sx={{ fontSize: '8pt' }} />
                            <div>{item.counters.likesCount.toLocaleString()} Likes</div>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <WatchIcon sx={{ fontSize: '8pt' }} />
                            <div>{item.duration ? secondsToMinutesAndSecondsString(item.duration) : '-'}</div>
                        </Stack>
                    </Stack>
                }
                actionIcon={mediaTypeIcon(item.type, theme)}
                onClick={() => {
                    handleMediaClicked(item);
                }}
                sx={{
                    backgroundColor: foundMedia ? alpha(theme.palette.primary.main, 0.8) : '#00000080',
                    '.MuiImageListItemBar-actionIcon': {
                        marginRight: theme.spacing(0.5),
                    },
                    '.MuiImageListItemBar-title': {
                        ...(!isLargeScreen && {
                            fontSize: '0.8rem',
                        }),
                    },
                    '.MuiImageListItemBar-titleWrap': {
                        padding: theme.spacing(1),
                    },
                }}
            />
        </ImageListItem>
    );
};

export default MessageUsersFormAddMediasSelectorImageListItem;
